/**
 * "puzzle": the selected image cut into grid.x × grid.y jigsaw pieces
 * (PlayspacePuzzle.cs). The pieces are cut with the app's own masks
 * (Resources/Puzzle/Masks, SetPiecesMasks): 512×512 images with a 362 px body
 * and 75 px on each side for the tabs; the pieces alternate like a
 * chessboard, so every inner edge is a tab on one side and a hole on the
 * other. A one-row or one-column puzzle has no mask for its pieces and keeps
 * rectangles. A faded preview of the whole image is shown in the board, with
 * the outline of the missing pieces; pieces are offered one at a time on the
 * right and must be dropped on their place. Items = number of pieces.
 *
 * The board is centred on the stage with the image's proportions, as Unity
 * lays it out (WorkspacePuzzle.SetUp): the image plus a tab's margin on each
 * side fits 1000 px.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { SlideOf } from "@makeit/schema";
import { useActivity } from "../activity";
import { useDraggable, useDropZone } from "../drag";
import { libraryUrl, mediaUrl } from "../../data/media";
import { usePlayer } from "../context";
import { STAGE_H, STAGE_W } from "../Stage";
import { shuffled } from "../util";

/** the tabs' share of a piece's body (75 px of a 362 px body in the masks) */
const TAB = 75 / 362;

/** The board for an image of this width/height ratio, centred on the stage. */
function puzzleBoard(aspect: number, cols: number, rows: number) {
  const scale = 1000 / Math.max(aspect * (1 + (2 * TAB) / cols), 1 + (2 * TAB) / rows);
  const w = aspect * scale;
  const h = scale;
  return { x: (STAGE_W - w) / 2, y: (STAGE_H - h) / 2, w, h };
}

/** The image's width/height ratio once it has loaded. */
function useImageAspect(src: string): number | null {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.onload = () => img.naturalWidth && img.naturalHeight && setAspect(img.naturalWidth / img.naturalHeight);
    img.src = src;
  }, [src]);
  return aspect;
}

type Side = "tab" | "hole" | "flat";
/** The masks by their sides (top, right, bottom, left), read from the images. */
const MASKS: [number, Side, Side, Side, Side][] = [
  [0, "flat", "hole", "tab", "flat"],
  [1, "flat", "tab", "hole", "tab"],
  [2, "flat", "hole", "tab", "hole"],
  [3, "flat", "flat", "hole", "tab"],
  [4, "tab", "hole", "tab", "hole"],
  [5, "hole", "tab", "hole", "tab"],
  [6, "tab", "hole", "flat", "flat"],
  [7, "hole", "tab", "flat", "flat"],
  [8, "tab", "flat", "flat", "hole"],
  [9, "hole", "flat", "flat", "tab"],
  [10, "flat", "flat", "tab", "hole"],
  [11, "hole", "tab", "hole", "flat"],
  [12, "tab", "flat", "tab", "hole"],
  [13, "tab", "hole", "tab", "flat"],
  [14, "hole", "flat", "hole", "tab"],
  [15, "tab", "hole", "flat", "hole"],
  [16, "hole", "tab", "flat", "tab"],
];

/** The mask of the piece at (x, y), y = 0 at the top; null when there is none (rectangle). */
export function puzzleMask(x: number, y: number, cols: number, rows: number): number | null {
  const even = (x + y) % 2 === 0;
  const inner: Side[] = even ? ["tab", "hole", "tab", "hole"] : ["hole", "tab", "hole", "tab"];
  const border = [y === 0, x === cols - 1, y === rows - 1, x === 0];
  const sides = inner.map((s, i) => (border[i] ? "flat" : s));
  return MASKS.find(([, ...m]) => m.every((s, i) => s === sides[i]))?.[0] ?? null;
}

export function PuzzleSlide({ slide }: { slide: SlideOf<"puzzle"> }) {
  const { project } = usePlayer();
  const cols = Math.max(1, slide.grid.x || Math.round(Math.sqrt(slide.pieces.length)) || 2);
  const rows = Math.max(1, slide.grid.y || Math.ceil(slide.pieces.length / cols) || 2);
  const total = cols * rows;
  const activity = useActivity(total);
  const src = mediaUrl(slide.selectedImage?.image, "image", project.id) ?? "";
  const order = useMemo(() => shuffled(Array.from({ length: total }, (_, i) => i)), [slide, total]);
  const [placed, setPlaced] = useState<Set<number>>(new Set());
  const [cursor, setCursor] = useState(0);

  // until the image loads, the grid's ratio (Unity picks the grid by the image's)
  const board = puzzleBoard(useImageAspect(src) ?? cols / rows, cols, rows);
  const cellW = board.w / cols;
  const cellH = board.h / rows;
  const next = order.slice(cursor).find((i) => !placed.has(i));
  const masks = useMemo(() => Array.from({ length: total }, (_, i) => puzzleMask(i % cols, Math.floor(i / cols), cols, rows)), [total, cols, rows]);

  const drop = (piece: number, zone: string | null) => {
    if (zone === `cell:${piece}`) {
      setPlaced((p) => new Set([...p, piece]));
      setCursor((c) => c + 1);
      activity.correct();
      return true;
    }
    if (zone) activity.wrong(true);
    return false;
  };

  /** Piece i at `scale` of its size on the board: the body plus the tab margins, cut by its mask. */
  const pieceStyle = (i: number, scale: number, withImage = true): React.CSSProperties => {
    const cx = i % cols;
    const cy = Math.floor(i / cols);
    const w = cellW * scale;
    const h = cellH * scale;
    const mask = masks[i];
    const mx = mask === null ? 0 : w * TAB;
    const my = mask === null ? 0 : h * TAB;
    const maskUrl = mask === null ? undefined : `url("${libraryUrl(`ui/puzzleMask${mask}.png`)}")`;
    return {
      width: w + 2 * mx,
      height: h + 2 * my,
      ...(withImage && {
        backgroundImage: `url("${src}")`,
        backgroundSize: `${cellW * cols * scale}px ${cellH * rows * scale}px`,
        backgroundPosition: `${mx - cx * w}px ${my - cy * h}px`,
        backgroundRepeat: "no-repeat",
      }),
      ...(maskUrl && { maskImage: maskUrl, WebkitMaskImage: maskUrl, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }),
    };
  };
  /** Where a piece sits in its cell (the tab margins stick out). */
  const inCell = (i: number): React.CSSProperties => (masks[i] === null ? { left: 0, top: 0 } : { left: -cellW * TAB, top: -cellH * TAB });
  // the pieces are offered right of the board, as big as fits there
  const trayX = board.x + board.w + 60;
  const trayW = STAGE_W - 60 - trayX;
  const traySize = Math.min(1, 480 / Math.max(cellW, cellH), (trayW - 20) / (cellW * (1 + 2 * TAB)));

  return (
    <div className="puzzle">
      <div className="puzzle-board" style={{ left: board.x, top: board.y, width: board.w, height: board.h, backgroundImage: `url("${src}")` }}>
        {Array.from({ length: total }, (_, i) => (
          <Cell key={i} index={i} style={{ left: (i % cols) * cellW, top: Math.floor(i / cols) * cellH, width: cellW, height: cellH }} filled={placed.has(i)} shape={{ ...pieceStyle(i, 1, placed.has(i)), ...inCell(i) }} />
        ))}
      </div>
      <div className="puzzle-tray" style={{ left: trayX }}>
        {next !== undefined && <Piece key={next} index={next} style={pieceStyle(next, traySize)} onDrop={(z) => drop(next, z)} />}
        <div className="puzzle-count">{placed.size} / {total}</div>
      </div>
    </div>
  );
}

/** A place on the board: the outline of the missing piece, or the piece once placed. */
function Cell({ index, style, filled, shape }: { index: number; style: React.CSSProperties; filled: boolean; shape: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useDropZone(`cell:${index}`, ref, !filled);
  return (
    <div ref={ref} data-zone={`cell:${index}`} className={`puzzle-cell ${filled ? "filled" : ""}`} style={style}>
      <div className="puzzle-shape">
        <div className="puzzle-piece-img" style={shape} />
      </div>
    </div>
  );
}

function Piece({ index, style, onDrop }: { index: number; style: React.CSSProperties; onDrop: (z: string | null) => boolean }) {
  const drag = useDraggable({ onDrop });
  // the shadow goes on a wrapper: on the masked element itself it would be cut away
  return (
    <div className="puzzle-piece" data-piece={index} style={drag.style} {...drag.handlers}>
      <div className="puzzle-piece-img" style={style} />
    </div>
  );
}
